import { User } from "@/types/user";
import { useIdentity } from "@/utils/identity";
import { PresenceChannel } from "laravel-echo";
import { useEffect, useRef, useState } from "react";

export function usePresence(user: User | null) {
    // connect to the user's presence channel

    const { twoFactorPassed } = useIdentity();

    useEffect(() => {
        if (!user || !twoFactorPassed) return;
        const channel = `App.Models.${user.type === "client" ? "Client" : "Coach"}.Presence.${user.id}`;
        window.Echo.join(channel);
        return () => {
            window.Echo.leaveChannel(channel);
        };
    }, [user?.id, user?.type, twoFactorPassed]);
}

type VideoRoomPresenceData = {
    type: "client" | "coach";
};
export class VideoRoomPresence {
    /**
     * This seems weird but the reason we do it this way is so that each video room gets a single instance.
     * That way, we can subscribe to the presence of a video room in multiple components, while only needing
     * to set up a single websocket connection.
     */
    static instances: {
        [room: string]: VideoRoomPresence;
    } = {};
    channel: PresenceChannel | null = null;
    presentUsers: Set<VideoRoomPresenceData["type"]> = new Set();
    subscriptions: Set<(users: typeof this.presentUsers) => void> = new Set();
    myPresence: VideoRoomPresenceData["type"] | null = null;

    constructor(videoRoomName: string) {
        if (VideoRoomPresence.instances[videoRoomName]) {
            return VideoRoomPresence.instances[videoRoomName];
        }
        VideoRoomPresence.instances[videoRoomName] = this;
        this.channel = window.Echo.join(
            `App.Models.VideoRoom.Presence.${videoRoomName}`,
        );
        this.channel
            .leaving((data: VideoRoomPresenceData) => {
                this.removeUser(data.type);
            })
            .joining(() => {
                if (this.myPresence) {
                    // When someone new joins, let them know you're already there
                    this.channel?.whisper("joined", {
                        type: this.myPresence,
                    });
                }
            })
            .listenForWhisper("joined", (data: VideoRoomPresenceData) => {
                this.addUser(data.type);
            })
            .listenForWhisper("left", (data: VideoRoomPresenceData) => {
                this.removeUser(data.type);
            });
    }

    addUser(type: VideoRoomPresenceData["type"]) {
        if (this.presentUsers.has(type)) {
            return;
        }
        this.presentUsers.add(type);
        this.subscriptions.forEach((callback) => callback(this.presentUsers));
    }

    removeUser(type: VideoRoomPresenceData["type"]) {
        if (!this.presentUsers.has(type)) {
            return;
        }
        this.presentUsers.delete(type);
        this.subscriptions.forEach((callback) => callback(this.presentUsers));
    }

    joinedRoom(userType: VideoRoomPresenceData["type"]) {
        this.myPresence = userType;
        this.channel?.whisper("joined", {
            type: userType,
        });
        this.addUser(userType);
    }

    leftRoom(userType: VideoRoomPresenceData["type"]) {
        this.myPresence = null;
        this.channel?.whisper("left", {
            type: userType,
        });
        this.removeUser(userType);
    }

    subscribe(
        callback: (users: typeof this.presentUsers) => void,
        knownUserCount?: number,
    ) {
        this.subscriptions.add(callback);
        if (knownUserCount !== this.presentUsers.size) {
            callback(this.presentUsers);
        }
    }

    unsubscribe(callback: (users: typeof this.presentUsers) => void) {
        this.subscriptions.delete(callback);
    }
}

export function useVideoRoomPresence(videoRoomName?: string) {
    const { user } = useIdentity();

    const presence = useRef<VideoRoomPresence>(null);
    const [presentUsers, setPresentUsers] = useState<
        VideoRoomPresenceData["type"][]
    >([]);

    useEffect(() => {
        if (!videoRoomName) return;
        presence.current = new VideoRoomPresence(videoRoomName);
        const handler = (users: Set<VideoRoomPresenceData["type"]>) => {
            setPresentUsers([...users]);
        };
        presence.current.subscribe(handler, presentUsers.length);
        return () => presence.current?.unsubscribe(handler);
    }, [videoRoomName]);

    return {
        presentUsers,
        joinedRoom: () => presence.current?.joinedRoom(user?.type ?? "client"),
        leftRoom: () => presence.current?.leftRoom(user?.type ?? "client"),
    };
}
